import { useCallback } from 'react';
import {
  useGetEmailPulseSummaryQuery as useGetEmailPulseSummaryQueryBase,
  useGetEmailPulseEmailsOpenedBreakdownQuery as useGetEmailPulseEmailsOpenedBreakdownQueryBase,
  useGetEmailPulseEmailsBouncedBreakdownQuery as useGetEmailPulseEmailsBouncedBreakdownQueryBase,
  useGetEmailPulseTrendsQuery as useGetEmailPulseTrendsQueryBase,
  useGetEmailPulseDataQuery as useGetEmailPulseDataQueryBase,
  useExportEmailPulseMutation as useExportEmailPulseMutationBase,
} from './reports-service';
import type { EmailPulseExportParams } from './reports-service.types';

/**
 * Transform frontend search params to API params
 * - Passes through dates as-is (startdate, enddate, comparisonstartdate, comparisonenddate)
 * - Filters out 'ALL' values
 * - Converts string arrays to proper arrays
 */
export const transformParams = (params: any) => {
  const transformed: any = {};

  // Pass through dates as-is
  if (params.startdate) {
    transformed.startdate = params.startdate;
  }
  if (params.enddate) {
    transformed.enddate = params.enddate;
  }
  if (params.comparisonstartdate) {
    transformed.comparisonstartdate = params.comparisonstartdate;
  }
  if (params.comparisonenddate) {
    transformed.comparisonenddate = params.comparisonenddate;
  }

  // Handle storeIds array
  if (params.storeIds) {
    const storeIds = Array.isArray(params.storeIds)
      ? params.storeIds
      : params.storeIds.split(',').filter(Boolean);
    // Only include if not 'ALL'
    if (storeIds.length > 0 && !storeIds.includes('ALL')) {
      transformed.storeIds = storeIds;
    }
  }

  // Handle campaignIds array
  if (params.campaignIds) {
    const campaignIds = Array.isArray(params.campaignIds)
      ? params.campaignIds
      : params.campaignIds.split(',').filter(Boolean);
    // Only include if not 'ALL'
    if (campaignIds.length > 0 && !campaignIds.includes('ALL')) {
      transformed.campaignIds = campaignIds;
    }
  }

  // Handle messageIds array
  if (params.messageIds) {
    const messageIds = Array.isArray(params.messageIds)
      ? params.messageIds
      : params.messageIds.split(',').filter(Boolean);
    // Only include if not 'ALL'
    if (messageIds.length > 0 && !messageIds.includes('ALL')) {
      transformed.messageIds = messageIds;
    }
  }

  // Handle offerIds array
  if (params.offerIds) {
    const offerIds = Array.isArray(params.offerIds)
      ? params.offerIds
      : params.offerIds.split(',').filter(Boolean);
    // Only include if not 'ALL'
    if (offerIds.length > 0 && !offerIds.includes('ALL')) {
      transformed.offerIds = offerIds;
    }
  }

  // Handle status
  if (params.status && params.status !== 'ALL') {
    transformed.status = params.status;
  }

  // Handle emailType
  if (params.emailType && params.emailType !== 'ALL') {
    transformed.emailType = params.emailType;
  }

  // Pass through any other params that aren't frontend-specific
  Object.keys(params).forEach(key => {
    if (
      ![
        'startdate',
        'enddate',
        'comparisonstartdate',
        'comparisonenddate',
        'storeIds',
        'campaignIds',
        'messageIds',
        'offerIds',
        'status',
        'emailType',
      ].includes(key)
    ) {
      transformed[key] = params[key];
    }
  });

  return transformed;
};

// Helper to create wrapped query hook with parameter transformation
const createQueryHook = (reportType: string, baseHook: any) => {
  return (params: any) => {
    const transformedParams = transformParams(params);
    return baseHook({ ...transformedParams, reportType });
  };
};

// Helper to create wrapped mutation hook with proper types
export const createMutationHook = (reportType: string) => {
  return () => {
    const [exportMutation, result] = useExportEmailPulseMutationBase();
    const wrappedExport = useCallback(
      (params: Omit<EmailPulseExportParams, 'reportType'>) =>
        exportMutation({ ...params, reportType } as EmailPulseExportParams & {
          reportType: string;
        }),
      [exportMutation],
    );
    return [wrappedExport, result] as const;
  };
};

// Overview hooks
export const useGetEmailPulseOverviewSummaryQuery = createQueryHook(
  'OVERVIEW',
  useGetEmailPulseSummaryQueryBase,
);

export const useGetEmailPulseEmailsOpenedBreakdownQuery = createQueryHook(
  'OVERVIEW',
  useGetEmailPulseEmailsOpenedBreakdownQueryBase,
);

export const useGetEmailPulseEmailsBouncedBreakdownQuery = createQueryHook(
  'OVERVIEW',
  useGetEmailPulseEmailsBouncedBreakdownQueryBase,
);

export const useGetEmailPulseOverviewTrendsQuery =
  useGetEmailPulseTrendsQueryBase;

export const useGetEmailPulseOverviewDataQuery = createQueryHook(
  'OVERVIEW',
  useGetEmailPulseDataQueryBase,
);

export const useExportEmailPulseOverviewMutation =
  createMutationHook('OVERVIEW');

// Messages hooks
export const useGetEmailPulseMarketingSummaryQuery = createQueryHook(
  'MESSAGES',
  useGetEmailPulseSummaryQueryBase,
);

export const useGetEmailPulseMarketingEmailsOpenedBreakdownQuery =
  createQueryHook('MESSAGES', useGetEmailPulseEmailsOpenedBreakdownQueryBase);

export const useGetEmailPulseMarketingEmailsBouncedBreakdownQuery =
  createQueryHook('MESSAGES', useGetEmailPulseEmailsBouncedBreakdownQueryBase);

export const useGetEmailPulseMarketingDataQuery = createQueryHook(
  'MESSAGES',
  useGetEmailPulseDataQueryBase,
);

export const useExportEmailPulseMarketingMutation =
  createMutationHook('MESSAGES');

// Offers hooks
export const useGetEmailPulseOffersSummaryQuery = createQueryHook(
  'OFFERS',
  useGetEmailPulseSummaryQueryBase,
);

export const useGetEmailPulseOffersEmailsOpenedBreakdownQuery = createQueryHook(
  'OFFERS',
  useGetEmailPulseEmailsOpenedBreakdownQueryBase,
);

export const useGetEmailPulseOffersEmailsBouncedBreakdownQuery =
  createQueryHook('OFFERS', useGetEmailPulseEmailsBouncedBreakdownQueryBase);

export const useGetEmailPulseOffersDataQuery = createQueryHook(
  'OFFERS',
  useGetEmailPulseDataQueryBase,
);

export const useExportEmailPulseOffersMutation = createMutationHook('OFFERS');

// Orders hooks
export const useGetEmailPulseOrdersSummaryQuery = createQueryHook(
  'ORDERS',
  useGetEmailPulseSummaryQueryBase,
);

export const useGetEmailPulseOrdersEmailsOpenedBreakdownQuery = createQueryHook(
  'ORDERS',
  useGetEmailPulseEmailsOpenedBreakdownQueryBase,
);

export const useGetEmailPulseOrdersEmailsBouncedBreakdownQuery =
  createQueryHook('ORDERS', useGetEmailPulseEmailsBouncedBreakdownQueryBase);

export const useGetEmailPulseOrdersDataQuery = createQueryHook(
  'ORDERS',
  useGetEmailPulseDataQueryBase,
);

export const useExportEmailPulseOrdersMutation = createMutationHook('ORDERS');

// Gift Cards hooks
export const useGetEmailPulseGiftCardsSummaryQuery = createQueryHook(
  'GIFT_CARDS',
  useGetEmailPulseSummaryQueryBase,
);

export const useGetEmailPulseGiftCardsEmailsOpenedBreakdownQuery =
  createQueryHook('GIFT_CARDS', useGetEmailPulseEmailsOpenedBreakdownQueryBase);

export const useGetEmailPulseGiftCardsEmailsBouncedBreakdownQuery =
  createQueryHook(
    'GIFT_CARDS',
    useGetEmailPulseEmailsBouncedBreakdownQueryBase,
  );

export const useGetEmailPulseGiftCardsDataQuery = createQueryHook(
  'GIFT_CARDS',
  useGetEmailPulseDataQueryBase,
);

export const useExportEmailPulseGiftCardsMutation =
  createMutationHook('GIFT_CARDS');

// Security hooks
export const useGetEmailPulseSecuritySummaryQuery = createQueryHook(
  'SECURITY',
  useGetEmailPulseSummaryQueryBase,
);

export const useGetEmailPulseSecurityEmailsOpenedBreakdownQuery =
  createQueryHook('SECURITY', useGetEmailPulseEmailsOpenedBreakdownQueryBase);

export const useGetEmailPulseSecurityEmailsBouncedBreakdownQuery =
  createQueryHook('SECURITY', useGetEmailPulseEmailsBouncedBreakdownQueryBase);

export const useGetEmailPulseSecurityDataQuery = createQueryHook(
  'SECURITY',
  useGetEmailPulseDataQueryBase,
);

export const useExportEmailPulseSecurityMutation =
  createMutationHook('SECURITY');
